// frontend/src/api/portfolio-import.ts
import apiClient from './client';

export type ImportSource = 'coinbase' | 'binance' | 'kraken' | 'koinly' | 'coingecko' | 'generic_csv' | 'gemini_csv' | 'crypto_com' | 'ledger_live';

export type ImportStatus = 'pending' | 'parsing' | 'preview' | 'importing' | 'complete' | 'failed';

export interface ParsedRowPreview {
  date: string;
  type: string;
  asset: string;
  amount: string;
  price: string;
  fee: string;
  rawIndex: number;
}

export interface ImportParseError {
  row: number;
  message: string;
}

export interface UploadResponse {
  jobId: string;
  source: ImportSource;
  filename: string;
  rowCount: number;
  preview: ParsedRowPreview[];
  errors: ImportParseError[];
  expiresAt: string;
}

export interface ConfirmResponse {
  imported: number;
  skipped: number;
  errors: number;
  jobId: string;
  completedAt?: string;
}

export interface ImportJob {
  id: string;
  source: ImportSource;
  filename: string;
  row_count: number | null;
  imported_count: number | null;
  skipped_count: number | null;
  error_count: number | null;
  status: ImportStatus;
  created_at: string;
  completed_at: string | null;
  reconciliation_job_id?: string | null;
  reconciliation_source_label?: string | null;
  latest_ledger_imported_at?: string | null;
}

export interface ImportHistoryResponse {
  jobs: ImportJob[];
}

/**
 * Upload a CSV file for import preview.
 * POST /blockchain/import/:userId/upload
 * Returns jobId + first 10 rows preview.
 */
export async function uploadImportFile(userId: string, file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post<UploadResponse>(`/blockchain/import/${userId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return res.data;
}

/**
 * Confirm an import job — materialise parsed rows into transaction_ledger.
 * POST /blockchain/import/:userId/confirm/:jobId
 */
export async function confirmImport(userId: string, jobId: string): Promise<ConfirmResponse> {
  const res = await apiClient.post<ConfirmResponse>(`/blockchain/import/${userId}/confirm/${jobId}`);
  return res.data;
}

/**
 * Fetch import job history for a user.
 * GET /blockchain/import/:userId/history
 */
export async function getImportHistory(userId: string): Promise<ImportHistoryResponse> {
  const res = await apiClient.get<ImportHistoryResponse>(`/blockchain/import/${userId}/history`);
  return res.data;
}

export const SOURCE_LABELS: Record<ImportSource, string> = {
  coinbase: 'Coinbase',
  binance: 'Binance',
  kraken: 'Kraken',
  koinly: 'Koinly',
  coingecko: 'CoinGecko',
  generic_csv: 'Generic CSV',
  gemini_csv: 'Gemini',
  crypto_com: 'Crypto.com',
  ledger_live: 'Ledger Live',
};

export const SOURCE_DESCRIPTIONS: Record<ImportSource, string> = {
  coinbase: 'Coinbase transaction history export. Download from Account → Statements.',
  binance: 'Binance transaction history. Export via Account → Transaction History → CSV.',
  kraken: 'Kraken ledger export. Download from History → Ledgers → Export.',
  koinly: 'Koinly universal CSV. Export from Koinly or any platform using Koinly format.',
  coingecko: 'CoinGecko portfolio export. Download from Portfolio → Export.',
  generic_csv: 'Generic CSV with date, asset, amount columns. Works with most exports.',
  gemini_csv: 'Gemini transaction history. Download from Account → Statements → Transaction History.',
  crypto_com: 'Crypto.com App CSV. Export from the App → Accounts → Transaction History.',
  ledger_live: 'Ledger Live export. Export from Ledger Live → Accounts → Export Operations History.',
};

export const SOURCE_FILE_TYPES: Record<ImportSource, string> = {
  coinbase: 'CSV',
  binance: 'CSV',
  kraken: 'CSV',
  koinly: 'CSV',
  coingecko: 'CSV',
  generic_csv: 'CSV',
  gemini_csv: 'CSV',
  crypto_com: 'CSV',
  ledger_live: 'CSV',
};
